import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon, CheckCircleIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, ZAxis, Cell } from 'recharts';
const ProcessParametersToolingDashboard: React.FC = () => {
  // KPI Data
  const kpis = [{
    title: 'Tool Life %',
    value: '78.5%',
    change: '+2.3%',
    isPositive: true,
    subtitle: 'Avg remaining life'
  }, {
    title: 'Parameter Drift Rate',
    value: '2.8%',
    change: '-0.5%',
    isPositive: true,
    subtitle: 'Within tolerance'
  }];
  // Tool Life Trend Data (Last 30 Days)
  const toolLifeTrendData = [{
    day: 'Day 1',
    Line1: 95,
    Line2: 92,
    Line3: 88,
    Line4: 90,
    Line5: 91,
    target: 80
  }, {
    day: 'Day 3',
    Line1: 92,
    Line2: 89,
    Line3: 85,
    Line4: 87,
    Line5: 88,
    target: 80
  }, {
    day: 'Day 5',
    Line1: 89,
    Line2: 86,
    Line3: 82,
    Line4: 84,
    Line5: 85,
    target: 80
  }, {
    day: 'Day 7',
    Line1: 86,
    Line2: 83,
    Line3: 79,
    Line4: 81,
    Line5: 82,
    target: 80
  }, {
    day: 'Day 9',
    Line1: 83,
    Line2: 80,
    Line3: 76,
    Line4: 78,
    Line5: 79,
    target: 80
  }, {
    day: 'Day 11',
    Line1: 80,
    Line2: 77,
    Line3: 73,
    Line4: 75,
    Line5: 76,
    target: 80
  }, {
    day: 'Day 13',
    Line1: 77,
    Line2: 74,
    Line3: 70,
    Line4: 72,
    Line5: 73,
    target: 80
  }, {
    day: 'Day 15',
    Line1: 74,
    Line2: 71,
    Line3: 95,
    Line4: 69,
    Line5: 70,
    target: 80
  }, {
    day: 'Day 17',
    Line1: 71,
    Line2: 68,
    Line3: 92,
    Line4: 66,
    Line5: 67,
    target: 80
  }, {
    day: 'Day 19',
    Line1: 68,
    Line2: 65,
    Line3: 89,
    Line4: 63,
    Line5: 64,
    target: 80
  }, {
    day: 'Day 21',
    Line1: 65,
    Line2: 62,
    Line3: 86,
    Line4: 60,
    Line5: 61,
    target: 80
  }, {
    day: 'Day 23',
    Line1: 62,
    Line2: 59,
    Line3: 83,
    Line4: 57,
    Line5: 58,
    target: 80
  }, {
    day: 'Day 25',
    Line1: 59,
    Line2: 56,
    Line3: 80,
    Line4: 54,
    Line5: 55,
    target: 80
  }, {
    day: 'Day 27',
    Line1: 56,
    Line2: 53,
    Line3: 77,
    Line4: 51,
    Line5: 52,
    target: 80
  }, {
    day: 'Day 29',
    Line1: 53,
    Line2: 50,
    Line3: 74,
    Line4: 48,
    Line5: 49,
    target: 80
  }, {
    day: 'Day 30',
    Line1: 50,
    Line2: 47,
    Line3: 71,
    Line4: 45,
    Line5: 46,
    target: 80
  }];
  // Process Parameter Variance Data
  const parameterVarianceData = [{
    parameter: 'Temperature',
    target: 180,
    actual: 182,
    variance: 1.1,
    tolerance: 5,
    status: 'normal'
  }, {
    parameter: 'Pressure',
    target: 150,
    actual: 148,
    variance: -1.3,
    tolerance: 5,
    status: 'normal'
  }, {
    parameter: 'Cycle Time',
    target: 45,
    actual: 46.5,
    variance: 3.3,
    tolerance: 5,
    status: 'warning'
  }, {
    parameter: 'Injection Speed',
    target: 100,
    actual: 102,
    variance: 2.0,
    tolerance: 5,
    status: 'normal'
  }, {
    parameter: 'Cooling Time',
    target: 30,
    actual: 31.8,
    variance: 6.0,
    tolerance: 5,
    status: 'critical'
  }, {
    parameter: 'Holding Pressure',
    target: 120,
    actual: 119,
    variance: -0.8,
    tolerance: 5,
    status: 'normal'
  }];
  // Parameter Variance Scatter Plot Data
  const parameterScatterData = [{
    parameter: 'Temperature',
    variance: 1.1,
    frequency: 145,
    tolerance: 5
  }, {
    parameter: 'Pressure',
    variance: -1.3,
    frequency: 132,
    tolerance: 5
  }, {
    parameter: 'Cycle Time',
    variance: 3.3,
    frequency: 168,
    tolerance: 5
  }, {
    parameter: 'Injection Speed',
    variance: 2.0,
    frequency: 152,
    tolerance: 5
  }, {
    parameter: 'Cooling Time',
    variance: 6.0,
    frequency: 95,
    tolerance: 5
  }, {
    parameter: 'Holding Pressure',
    variance: -0.8,
    frequency: 128,
    tolerance: 5
  }];
  // Alert Panel Data
  const alertsData = [{
    type: 'Critical',
    parameter: 'Cooling Time',
    line: 'Line 3',
    deviation: '+6.0%',
    message: 'Cooling time exceeding tolerance on Line 3',
    timestamp: '2 minutes ago',
    action: 'Adjust cooling system'
  }, {
    type: 'Warning',
    parameter: 'Cycle Time',
    line: 'Line 2',
    deviation: '+3.3%',
    message: 'Cycle time approaching upper tolerance limit',
    timestamp: '15 minutes ago',
    action: 'Monitor and review'
  }, {
    type: 'Warning',
    parameter: 'Tool Life',
    line: 'Line 2',
    deviation: '47% remaining',
    message: 'Tool replacement needed within 48 hours',
    timestamp: '1 hour ago',
    action: 'Schedule tool change'
  }, {
    type: 'Info',
    parameter: 'Temperature',
    line: 'Line 1',
    deviation: '+1.1%',
    message: 'Slight temperature increase detected',
    timestamp: '2 hours ago',
    action: 'Continue monitoring'
  }];
  // Tooling Health Summary Table Data
  const toolingHealthData = [{
    line: 'Line 1',
    toolId: 'T-001',
    toolType: 'Injection Mold',
    currentLife: 50,
    targetLife: 100,
    lifeRemaining: 50,
    cyclesCompleted: 125000,
    targetCycles: 250000,
    lastMaintenance: '2024-01-15',
    nextMaintenance: '2024-02-15',
    status: 'Warning',
    condition: 'Fair',
    replacementCost: 125000,
    downtime: 4
  }, {
    line: 'Line 2',
    toolId: 'T-002',
    toolType: 'Injection Mold',
    currentLife: 47,
    targetLife: 100,
    lifeRemaining: 47,
    cyclesCompleted: 132500,
    targetCycles: 250000,
    lastMaintenance: '2024-01-12',
    nextMaintenance: '2024-02-10',
    status: 'Critical',
    condition: 'Poor',
    replacementCost: 125000,
    downtime: 4
  }, {
    line: 'Line 3',
    toolId: 'T-003',
    toolType: 'Injection Mold',
    currentLife: 71,
    targetLife: 100,
    lifeRemaining: 71,
    cyclesCompleted: 72500,
    targetCycles: 250000,
    lastMaintenance: '2024-01-28',
    nextMaintenance: '2024-03-15',
    status: 'Good',
    condition: 'Good',
    replacementCost: 125000,
    downtime: 4
  }, {
    line: 'Line 4',
    toolId: 'T-004',
    toolType: 'Injection Mold',
    currentLife: 45,
    targetLife: 100,
    lifeRemaining: 45,
    cyclesCompleted: 137500,
    targetCycles: 250000,
    lastMaintenance: '2024-01-10',
    nextMaintenance: '2024-02-08',
    status: 'Critical',
    condition: 'Poor',
    replacementCost: 125000,
    downtime: 4
  }, {
    line: 'Line 5',
    toolId: 'T-005',
    toolType: 'Injection Mold',
    currentLife: 46,
    targetLife: 100,
    lifeRemaining: 46,
    cyclesCompleted: 135000,
    targetCycles: 250000,
    lastMaintenance: '2024-01-11',
    nextMaintenance: '2024-02-09',
    status: 'Warning',
    condition: 'Fair',
    replacementCost: 125000,
    downtime: 4
  }, {
    line: 'Line 1',
    toolId: 'T-006',
    toolType: 'Cutting Tool',
    currentLife: 82,
    targetLife: 100,
    lifeRemaining: 82,
    cyclesCompleted: 45000,
    targetCycles: 250000,
    lastMaintenance: '2024-01-25',
    nextMaintenance: '2024-03-10',
    status: 'Good',
    condition: 'Excellent',
    replacementCost: 45000,
    downtime: 2
  }, {
    line: 'Line 2',
    toolId: 'T-007',
    toolType: 'Cutting Tool',
    currentLife: 65,
    targetLife: 100,
    lifeRemaining: 65,
    cyclesCompleted: 87500,
    targetCycles: 250000,
    lastMaintenance: '2024-01-20',
    nextMaintenance: '2024-02-28',
    status: 'Good',
    condition: 'Good',
    replacementCost: 45000,
    downtime: 2
  }, {
    line: 'Line 3',
    toolId: 'T-008',
    toolType: 'Cutting Tool',
    currentLife: 58,
    targetLife: 100,
    lifeRemaining: 58,
    cyclesCompleted: 105000,
    targetCycles: 250000,
    lastMaintenance: '2024-01-18',
    nextMaintenance: '2024-02-22',
    status: 'Warning',
    condition: 'Fair',
    replacementCost: 45000,
    downtime: 2
  }, {
    line: 'Line 4',
    toolId: 'T-009',
    toolType: 'Cutting Tool',
    currentLife: 88,
    targetLife: 100,
    lifeRemaining: 88,
    cyclesCompleted: 30000,
    targetCycles: 250000,
    lastMaintenance: '2024-01-30',
    nextMaintenance: '2024-03-20',
    status: 'Good',
    condition: 'Excellent',
    replacementCost: 45000,
    downtime: 2
  }, {
    line: 'Line 5',
    toolId: 'T-010',
    toolType: 'Cutting Tool',
    currentLife: 72,
    targetLife: 100,
    lifeRemaining: 72,
    cyclesCompleted: 70000,
    targetCycles: 250000,
    lastMaintenance: '2024-01-22',
    nextMaintenance: '2024-03-05',
    status: 'Good',
    condition: 'Good',
    replacementCost: 45000,
    downtime: 2
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Critical':
        return 'bg-red-100 text-red-700';
      case 'Warning':
        return 'bg-yellow-100 text-yellow-700';
      case 'Good':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'Excellent':
        return 'bg-green-100 text-green-700';
      case 'Good':
        return 'bg-blue-100 text-blue-700';
      case 'Fair':
        return 'bg-yellow-100 text-yellow-700';
      case 'Poor':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getAlertColor = (type: string) => {
    switch (type) {
      case 'Critical':
        return 'border-red-500 bg-red-50';
      case 'Warning':
        return 'border-yellow-500 bg-yellow-50';
      case 'Info':
        return 'border-blue-500 bg-blue-50';
      default:
        return 'border-gray-500 bg-gray-50';
    }
  };
  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'Critical':
        return <AlertTriangleIcon className="text-red-600" size={20} data-id="element-6011" />;
      case 'Warning':
        return <AlertTriangleIcon className="text-yellow-600" size={20} data-id="element-6012" />;
      case 'Info':
        return <CheckCircleIcon className="text-blue-600" size={20} data-id="element-6013" />;
      default:
        return <CheckCircleIcon className="text-gray-600" size={20} data-id="element-6014" />;
    }
  };
  const getVarianceColor = (variance: number, tolerance: number) => {
    const absVariance = Math.abs(variance);
    if (absVariance > tolerance) return '#ef4444'; // red
    if (absVariance > tolerance * 0.6) return '#f59e0b'; // yellow
    return '#10b981'; // green
  };
  return <div data-id="element-6015">
      <DescriptionPanel title="Process Parameters & Tooling Management" description="Real-time monitoring and analysis of critical process parameters and tooling health. Track tool life, parameter drift, and deviations to ensure optimal production quality and minimize unplanned downtime." insights={['Line 2 and Line 4 tools at critical life (47% and 45%) - immediate replacement required', 'Cooling time on Line 3 exceeding tolerance by 6% - requires immediate adjustment', 'Overall tool life at 78.5% - improved by 2.3% through predictive maintenance', 'Parameter drift rate reduced to 2.8% - within acceptable tolerance range']} data-id="element-6016" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-6017">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-6018">
          Process & Tooling Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6019">
          {kpis.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-gray-50" data-id="element-6020">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-6021">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-2" data-id="element-6022">{kpi.subtitle}</div>
              <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-6023">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-6024">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-6025" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-6026" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* Alert Panel for Deviations */}
      <Card className="p-6 mb-8" data-id="element-6027">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-6028">
          Active Alerts & Deviations
        </h3>
        <div className="space-y-3" data-id="element-6029">
          {alertsData.map((alert, index) => <div key={index} className={`border-l-4 ${getAlertColor(alert.type)} p-4 rounded-r-lg`} data-id="element-6030">
              <div className="flex items-start justify-between" data-id="element-6031">
                <div className="flex items-start space-x-3" data-id="element-6032">
                  <div className="mt-0.5" data-id="element-6033">{getAlertIcon(alert.type)}</div>
                  <div data-id="element-6034">
                    <div className="flex items-center space-x-2 mb-1" data-id="element-6035">
                      <span className="text-sm font-bold text-gray-900" data-id="element-6036">
                        {alert.parameter}
                      </span>
                      <span className="text-xs text-gray-600" data-id="element-6037">•</span>
                      <span className="text-xs font-medium text-gray-700" data-id="element-6038">
                        {alert.line}
                      </span>
                      <span className="text-xs text-gray-600" data-id="element-6039">•</span>
                      <span className="text-xs font-semibold text-gray-900" data-id="element-6040">
                        {alert.deviation}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-1" data-id="element-6041">
                      {alert.message}
                    </p>
                    <div className="flex items-center space-x-4 text-xs text-gray-600" data-id="element-6042">
                      <span data-id="element-6043">{alert.timestamp}</span>
                      <span data-id="element-6044">•</span>
                      <span className="font-medium" data-id="element-6045">
                        Action: {alert.action}
                      </span>
                    </div>
                  </div>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${alert.type === 'Critical' ? 'bg-red-100 text-red-700' : alert.type === 'Warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-6046">
                  {alert.type}
                </span>
              </div>
            </div>)}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-6047">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-6048">
            <div className="text-center" data-id="element-6049">
              <div className="text-gray-600" data-id="element-6050">Critical Alerts</div>
              <div className="text-xl font-bold text-red-600" data-id="element-6051">1</div>
              <div className="text-xs text-gray-500" data-id="element-6052">requires action</div>
            </div>
            <div className="text-center" data-id="element-6053">
              <div className="text-gray-600" data-id="element-6054">Warnings</div>
              <div className="text-xl font-bold text-yellow-600" data-id="element-6055">2</div>
              <div className="text-xs text-gray-500" data-id="element-6056">monitor closely</div>
            </div>
            <div className="text-center" data-id="element-6057">
              <div className="text-gray-600" data-id="element-6058">Info</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-6059">1</div>
              <div className="text-xs text-gray-500" data-id="element-6060">for awareness</div>
            </div>
            <div className="text-center" data-id="element-6061">
              <div className="text-gray-600" data-id="element-6062">Response Time</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-6063">8 min</div>
              <div className="text-xs text-gray-500" data-id="element-6064">avg response</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Tool Life Trend */}
      <Card className="p-6 mb-8" data-id="element-6065">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-6066">
          Tool Life Trend (Last 30 Days)
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-6067">
          Remaining tool life percentage by production line
        </p>
        <ResponsiveContainer width="100%" height={400} data-id="element-6068">
          <LineChart data={toolLifeTrendData} data-id="element-6069">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-6070" />
            <XAxis dataKey="day" tick={{
            fontSize: 10
          }} data-id="element-6071" />
            <YAxis tick={{
            fontSize: 11
          }} domain={[0, 100]} data-id="element-6072" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} formatter={(value: number) => `${value}%`} data-id="element-6073" />
            <Legend data-id="element-6074" />
            <Line type="monotone" dataKey="Line1" stroke="#3b82f6" strokeWidth={2} name="Line 1" dot={{
            r: 2
          }} data-id="element-6075" />
            <Line type="monotone" dataKey="Line2" stroke="#10b981" strokeWidth={2} name="Line 2" dot={{
            r: 2
          }} data-id="element-6076" />
            <Line type="monotone" dataKey="Line3" stroke="#f59e0b" strokeWidth={2} name="Line 3" dot={{
            r: 2
          }} data-id="element-6077" />
            <Line type="monotone" dataKey="Line4" stroke="#ef4444" strokeWidth={2} name="Line 4" dot={{
            r: 2
          }} data-id="element-6078" />
            <Line type="monotone" dataKey="Line5" stroke="#8b5cf6" strokeWidth={2} name="Line 5" dot={{
            r: 2
          }} data-id="element-6079" />
            <Line type="monotone" dataKey="target" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" name="Replacement Threshold" dot={false} data-id="element-6080" />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-6081">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-6082">
            <div className="text-center" data-id="element-6083">
              <div className="text-gray-600" data-id="element-6084">Line 1</div>
              <div className="text-lg font-bold text-blue-600" data-id="element-6085">50%</div>
              <div className="text-xs text-gray-500" data-id="element-6086">warning level</div>
            </div>
            <div className="text-center" data-id="element-6087">
              <div className="text-gray-600" data-id="element-6088">Line 2</div>
              <div className="text-lg font-bold text-red-600" data-id="element-6089">47%</div>
              <div className="text-xs text-gray-500" data-id="element-6090">critical - replace</div>
            </div>
            <div className="text-center" data-id="element-6091">
              <div className="text-gray-600" data-id="element-6092">Line 3</div>
              <div className="text-lg font-bold text-green-600" data-id="element-6093">71%</div>
              <div className="text-xs text-gray-500" data-id="element-6094">good condition</div>
            </div>
            <div className="text-center" data-id="element-6095">
              <div className="text-gray-600" data-id="element-6096">Line 4</div>
              <div className="text-lg font-bold text-red-600" data-id="element-6097">45%</div>
              <div className="text-xs text-gray-500" data-id="element-6098">critical - replace</div>
            </div>
            <div className="text-center" data-id="element-6099">
              <div className="text-gray-600" data-id="element-6100">Line 5</div>
              <div className="text-lg font-bold text-yellow-600" data-id="element-6101">46%</div>
              <div className="text-xs text-gray-500" data-id="element-6102">warning level</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Process Parameter Variance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-6103">
        {/* Parameter Variance Chart */}
        <Card className="p-6" data-id="element-6104">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-6105">
            Process Parameter Variance
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-6106">
            Current variance from target values
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-6107">
            <BarChart data={parameterVarianceData} layout="vertical" data-id="element-6108">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-6109" />
              <XAxis type="number" tick={{
              fontSize: 11
            }} domain={[-10, 10]} data-id="element-6110" />
              <YAxis dataKey="parameter" type="category" width={100} tick={{
              fontSize: 11
            }} data-id="element-6111" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `${value.toFixed(1)}%`} data-id="element-6112" />
              <Bar dataKey="variance" name="Variance %" data-id="element-6113">
                {parameterVarianceData.map((entry, index) => <Cell key={`cell-${index}`} fill={getVarianceColor(entry.variance, entry.tolerance)} data-id="element-6114" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-6115">
            <div className="flex justify-between text-xs text-gray-600" data-id="element-6116">
              <div className="flex items-center" data-id="element-6117">
                <div className="w-3 h-3 bg-green-500 rounded mr-2" data-id="element-6118"></div>
                <span data-id="element-6119">Within Tolerance</span>
              </div>
              <div className="flex items-center" data-id="element-6120">
                <div className="w-3 h-3 bg-yellow-500 rounded mr-2" data-id="element-6121"></div>
                <span data-id="element-6122">Warning</span>
              </div>
              <div className="flex items-center" data-id="element-6123">
                <div className="w-3 h-3 bg-red-500 rounded mr-2" data-id="element-6124"></div>
                <span data-id="element-6125">Critical</span>
              </div>
            </div>
          </div>
        </Card>
        {/* Parameter Status Summary */}
        <Card className="p-6" data-id="element-6126">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-6127">
            Parameter Status Summary
          </h3>
          <div className="space-y-4" data-id="element-6128">
            {parameterVarianceData.map((item, index) => <div key={index} data-id="element-6129">
                <div className="flex items-center justify-between mb-2" data-id="element-6130">
                  <div className="flex items-center space-x-2" data-id="element-6131">
                    <span className="text-sm font-medium text-gray-700" data-id="element-6132">
                      {item.parameter}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${item.status === 'critical' ? 'bg-red-100 text-red-700' : item.status === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`} data-id="element-6133">
                      {item.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-right" data-id="element-6134">
                    <div className="text-sm font-semibold text-gray-900" data-id="element-6135">
                      {item.actual}{' '}
                      {item.parameter === 'Temperature' ? '°C' : item.parameter === 'Pressure' ? 'bar' : item.parameter.includes('Time') ? 's' : item.parameter.includes('Speed') ? 'mm/s' : ''}
                    </div>
                    <div className="text-xs text-gray-600" data-id="element-6136">
                      Target: {item.target}
                    </div>
                  </div>
                </div>
                <div className="relative h-2 bg-gray-100 rounded overflow-hidden" data-id="element-6137">
                  <div className={`h-full ${Math.abs(item.variance) > item.tolerance ? 'bg-red-500' : Math.abs(item.variance) > item.tolerance * 0.6 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{
                width: `${Math.min(Math.abs(item.variance) * 10, 100)}%`
              }} data-id="element-6138" />
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-500" data-id="element-6139">
                  <span data-id="element-6140">
                    Variance: {item.variance > 0 ? '+' : ''}
                    {item.variance.toFixed(1)}%
                  </span>
                  <span data-id="element-6141">Tolerance: ±{item.tolerance}%</span>
                </div>
              </div>)}
          </div>
        </Card>
      </div>
      {/* Tooling Health Summary Table */}
      <Card className="p-6" data-id="element-6142">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-6143">
          Tooling Health Summary
        </h3>
        <div className="overflow-x-auto" data-id="element-6144">
          <table className="w-full" data-id="element-6145">
            <thead data-id="element-6146">
              <tr className="border-b-2 border-gray-300" data-id="element-6147">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6148">
                  Line
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6149">
                  Tool ID
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6150">
                  Tool Type
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6151">
                  Life Remaining %
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6152">
                  Cycles Completed
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6153">
                  Target Cycles
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6154">
                  Last Maintenance
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6155">
                  Next Maintenance
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6156">
                  Status
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6157">
                  Condition
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6158">
                  Replacement Cost
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6159">
                  Downtime (hrs)
                </th>
              </tr>
            </thead>
            <tbody data-id="element-6160">
              {toolingHealthData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-6161">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-6162">
                    {row.line}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-6163">
                    {row.toolId}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-6164">
                    {row.toolType}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-6165">
                    <div className="flex flex-col items-center" data-id="element-6166">
                      <span className="text-sm font-semibold text-gray-900 mb-1" data-id="element-6167">
                        {row.lifeRemaining}%
                      </span>
                      <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-6168">
                        <div className={`h-2 rounded-full ${row.lifeRemaining >= 70 ? 'bg-green-500' : row.lifeRemaining >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                      width: `${row.lifeRemaining}%`
                    }} data-id="element-6169" />
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-6170">
                    {row.cyclesCompleted.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-6171">
                    {row.targetCycles.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-6172">
                    {row.lastMaintenance}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-6173">
                    {row.nextMaintenance}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-6174">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(row.status)}`} data-id="element-6175">
                      {row.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-6176">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getConditionColor(row.condition)}`} data-id="element-6177">
                      {row.condition}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-6178">
                    ₹{(row.replacementCost / 1000).toFixed(0)}K
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-6179">
                    {row.downtime}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-6180">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-6181">
            <div className="text-center" data-id="element-6182">
              <div className="text-gray-600" data-id="element-6183">Total Tools</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-6184">10</div>
              <div className="text-xs text-gray-500" data-id="element-6185">monitored</div>
            </div>
            <div className="text-center" data-id="element-6186">
              <div className="text-gray-600" data-id="element-6187">Critical Status</div>
              <div className="text-xl font-bold text-red-600" data-id="element-6188">2</div>
              <div className="text-xs text-gray-500" data-id="element-6189">replace now</div>
            </div>
            <div className="text-center" data-id="element-6190">
              <div className="text-gray-600" data-id="element-6191">Warning Status</div>
              <div className="text-xl font-bold text-yellow-600" data-id="element-6192">3</div>
              <div className="text-xs text-gray-500" data-id="element-6193">monitor</div>
            </div>
            <div className="text-center" data-id="element-6194">
              <div className="text-gray-600" data-id="element-6195">Good Status</div>
              <div className="text-xl font-bold text-green-600" data-id="element-6196">5</div>
              <div className="text-xs text-gray-500" data-id="element-6197">healthy</div>
            </div>
            <div className="text-center" data-id="element-6198">
              <div className="text-gray-600" data-id="element-6199">Avg Life Remaining</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-6200">61.9%</div>
              <div className="text-xs text-gray-500" data-id="element-6201">across all tools</div>
            </div>
            <div className="text-center" data-id="element-6202">
              <div className="text-gray-600" data-id="element-6203">Total Replacement Cost</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-6204">₹8.5L</div>
              <div className="text-xs text-gray-500" data-id="element-6205">for all tools</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default ProcessParametersToolingDashboard;